import type { ExternalDeviceCalendarEvent } from './deviceCalendarsTypes';

function dateKeyInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    calendar: 'gregory',
    numberingSystem: 'latn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function rangeInTimeZone(
  start: Date,
  end: Date,
  language: string,
  timeZone: string,
  includeDateOnSameDay = false
): string {
  const time = (date: Date) => date.toLocaleTimeString(language, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  if (dateKeyInTimeZone(start, timeZone) === dateKeyInTimeZone(end, timeZone)) {
    const times = `${time(start)}–${time(end)}`;
    return includeDateOnSameDay
      ? `${start.toLocaleDateString(language, {
        day: 'numeric',
        month: 'short',
        timeZone,
      })} · ${times}`
      : times;
  }
  const dateTime = (date: Date) => `${date.toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    timeZone,
  })} · ${time(date)}`;
  return `${dateTime(start)} – ${dateTime(end)}`;
}

export function formatExternalCalendarEventWhen(
  event: ExternalDeviceCalendarEvent,
  language: string,
  deviceTimeZone: string,
  allDayLabel: string
): string {
  if (event.allDay) return allDayLabel;
  let localRange: string;
  try {
    localRange = rangeInTimeZone(
      event.startDate,
      event.endDate,
      language,
      deviceTimeZone
    );
  } catch {
    const time = (date: Date) => date.toLocaleTimeString(language, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${time(event.startDate)}–${time(event.endDate)}`;
  }
  if (!event.timeZone || event.timeZone === deviceTimeZone) return localRange;
  try {
    const sourceRange = rangeInTimeZone(
      event.startDate,
      event.endDate,
      language,
      event.timeZone
    );
    return `${localRange} · ${sourceRange} (${event.timeZone})`;
  } catch {
    // Sağlayıcı bozuk/eskimiş bir IANA bölgesi döndürürse yerel saat yine
    // kullanılabilir kalır; takvim satırı bütünüyle kaybolmaz.
    return localRange;
  }
}

export function formatNosubjectCalendarEventWhen(
  input: {
    proposedAt: string;
    durationMinutes: number;
    sourceTimeZone?: string | null;
  },
  language: string,
  deviceTimeZone: string
): string {
  const start = new Date(input.proposedAt);
  const end = new Date(start.getTime() + Math.max(1, input.durationMinutes) * 60_000);
  try {
    const localRange = rangeInTimeZone(start, end, language, deviceTimeZone, true);
    if (!input.sourceTimeZone || input.sourceTimeZone === deviceTimeZone) return localRange;
    const sourceRange = rangeInTimeZone(
      start,
      end,
      language,
      input.sourceTimeZone,
      true
    );
    return `${localRange} · ${sourceRange} (${input.sourceTimeZone})`;
  } catch {
    return `${start.toLocaleDateString(language, {
      day: 'numeric',
      month: 'short',
    })} · ${start.toLocaleTimeString(language, {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }
}
